import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BACKEND_URL } from "@/config";

const AuthContext = createContext(null);

const API = `${BACKEND_URL}/api`;

/**
 * The backend returns an "anonymous guest" user with HTTP 200 for unauthenticated
 * requests when AUTH_ALLOW_ANONYMOUS=true (so anonymous analysis stays free).
 * This guest is NOT a real logged-in user, so we must not treat it as
 * authenticated on the client. Detect it by its sentinel identifiers.
 */
const ANON_GUEST_ID = "anonymous-guest";
const ANON_GUEST_EMAIL = "guest@example.com";

function isRealUser(u) {
  if (!u) return false;
  if (u.user_id === ANON_GUEST_ID) return false;
  if (u.email === ANON_GUEST_EMAIL) return false;
  return true;
}

// Configure axios
axios.defaults.withCredentials = true;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    // Skip if OAuth callback in progress
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    // Handle OAuth stored user
    const storedUser = localStorage.getItem("oauth_user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        localStorage.removeItem("oauth_user");
        if (isRealUser(parsed)) {
          setUser(parsed);
          setIsAuthenticated(true);
          setLoading(false);
          return;
        }
      } catch (e) {
        localStorage.removeItem("oauth_user");
      }
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      // With AUTH_ALLOW_ANONYMOUS the backend returns a guest user (HTTP 200)
      // even for a missing/invalid token. Only accept a real account.
      if (isRealUser(response.data)) {
        setUser(response.data);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem("token");
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch {
      localStorage.removeItem("token");
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      email,
      password,
    });

    setUser(response.data.user);
    setIsAuthenticated(true);
    localStorage.setItem("token", response.data.access_token);

    return response.data;
  };

  const adminLogin = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      email,
      password,
    });

    const loggedInUser = response.data.user;
    if (loggedInUser?.role !== "admin") {
      // Do not keep non-admin sessions for admin portal access.
      localStorage.removeItem("token");
      setUser(null);
      setIsAuthenticated(false);
      throw new Error("Admin access required");
    }

    setUser(loggedInUser);
    setIsAuthenticated(true);
    localStorage.setItem("token", response.data.access_token);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
    });

    setUser(response.data.user);
    setIsAuthenticated(true);
    localStorage.setItem("token", response.data.access_token);

    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }

    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem("token");
    navigate("/login");
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    adminLogin,
    register,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
